import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Modal,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  cn,
} from '@klaro7/ui';

import { CATEGORY_META, DEFAULT_CATEGORIES } from '../constants';
import { useCookieConsent } from '../hooks/useCookieConsent';
import { type CookieCategoryMap } from '../types';

function CategoryToggle({
  title,
  checked,
  locked,
  onChange,
}: {
  title: string;
  checked: boolean;
  locked?: boolean;
  onChange: (next: boolean) => void;
}) {
  const on = locked ? true : checked;
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={title}
      disabled={locked}
      onClick={() => !locked && onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-teal',
        on ? 'bg-brand-teal' : 'bg-neutral-300',
        locked && 'cursor-not-allowed opacity-60'
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform',
          on ? 'translate-x-5' : 'translate-x-1'
        )}
      />
    </button>
  );
}

/** Secondo layer — pannello preferenze granulari. */
export function CookiePreferencesModal() {
  const { consent, isPreferencesOpen, closePreferences, acceptAll, rejectAll, savePreferences } =
    useCookieConsent();

  const [draft, setDraft] = useState<CookieCategoryMap>({
    ...DEFAULT_CATEGORIES,
    ...(consent?.categories ?? {}),
  });

  // Riallinea i toggle allo stato salvato a ogni apertura.
  useEffect(() => {
    if (isPreferencesOpen) {
      setDraft({ ...DEFAULT_CATEGORIES, ...(consent?.categories ?? {}) });
    }
  }, [isPreferencesOpen, consent]);

  return (
    <Modal open={isPreferencesOpen} onOpenChange={(o) => !o && closePreferences()}>
      {/* Su schermi bassi (320x568) il pannello superava il viewport e la X di
          chiusura finiva fuori: sta dentro e scrolla. */}
      <ModalContent className="z-[70] max-h-[92vh] max-w-lg overflow-y-auto">
        <ModalHeader>
          <ModalTitle>Preferenze cookie</ModalTitle>
          <ModalDescription>
            I cookie tecnici sono sempre attivi perché necessari al funzionamento del sito. Per le
            altre categorie puoi attivare o disattivare il consenso in qualsiasi momento. Dettagli
            nella{' '}
            <Link to="/cookie-policy" className="font-medium text-brand-teal underline underline-offset-2">
              Cookie Policy
            </Link>
            .
          </ModalDescription>
        </ModalHeader>

        <div className="my-4 max-h-[45vh] space-y-3 overflow-y-auto pr-1">
          {CATEGORY_META.map((meta) => (
            <div
              key={meta.category}
              className="flex items-start justify-between gap-4 rounded-xl border border-neutral-200 p-4"
            >
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-neutral-900">{meta.title}</h3>
                  {meta.locked && (
                    <Badge className="border border-brand-teal/20 bg-brand-teal-50 text-brand-teal">
                      Sempre attivi
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-sm leading-relaxed text-neutral-600">{meta.description}</p>
              </div>
              <CategoryToggle
                title={meta.title}
                locked={meta.locked}
                checked={draft[meta.category]}
                onChange={(next) => setDraft((d) => ({ ...d, [meta.category]: next }))}
              />
            </div>
          ))}
        </div>

        <ModalFooter className="flex flex-col-reverse gap-2 border-t border-neutral-200 pt-4 sm:flex-row sm:justify-end sm:space-x-0">
          <Button
            onClick={rejectAll}
            className="bg-brand-teal font-semibold text-white hover:bg-brand-teal/90"
          >
            Rifiuta tutti
          </Button>
          <Button
            onClick={acceptAll}
            className="bg-brand-orange font-semibold text-white hover:bg-brand-orange/90"
          >
            Accetta tutti
          </Button>
          <Button
            onClick={() => savePreferences(draft)}
            variant="outline"
            className="border-2 border-brand-navy font-semibold text-brand-navy hover:bg-brand-navy/5"
          >
            Salva preferenze
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
